import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { userClient } from '../../lib/userClient'
import type {
  WaybookEvent,
  WaybookFile,
  WaybookNote,
  WaybookParticipant,
  WaybookPost,
  WaybookTrip,
} from '../../types'
import { Loader2, MapPin, Users } from 'lucide-react'

type TripTab = 'overview' | 'events' | 'posts' | 'files' | 'notes' | 'participants'

export default function UserTripDetailPage() {
  const { id } = useParams<{ id: string }>()

  const [trip, setTrip] = useState<WaybookTrip | null>(null)
  const [loadingTrip, setLoadingTrip] = useState(false)
  const [tripError, setTripError] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<TripTab>('overview')

  const [events, setEvents] = useState<WaybookEvent[]>([])
  const [eventsLoaded, setEventsLoaded] = useState(false)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState<string | null>(null)

  const [posts, setPosts] = useState<WaybookPost[]>([])
  const [postsLoaded, setPostsLoaded] = useState(false)
  const [postsLoading, setPostsLoading] = useState(false)
  const [postsError, setPostsError] = useState<string | null>(null)

  const [files, setFiles] = useState<WaybookFile[]>([])
  const [filesLoaded, setFilesLoaded] = useState(false)
  const [filesLoading, setFilesLoading] = useState(false)
  const [filesError, setFilesError] = useState<string | null>(null)

  const [notes, setNotes] = useState<WaybookNote[]>([])
  const [notesLoaded, setNotesLoaded] = useState(false)
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesError, setNotesError] = useState<string | null>(null)

  const [participants, setParticipants] = useState<WaybookParticipant[]>([])
  const [participantsLoaded, setParticipantsLoaded] = useState(false)
  const [participantsLoading, setParticipantsLoading] = useState(false)
  const [participantsError, setParticipantsError] = useState<string | null>(null)

  const fetchTrip = useCallback(async () => {
    if (!id) return
    setLoadingTrip(true)
    setTripError(null)
    try {
      const response = await userClient.getTripFull(id)
      const payload = response.data?.data as WaybookTrip | undefined
      setTrip(payload ?? null)
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load trip'
      setTripError(message)
    } finally {
      setLoadingTrip(false)
    }
  }, [id])

  useEffect(() => {
    fetchTrip()
  }, [fetchTrip])

  useEffect(() => {
    if (!id) return

    if (activeTab === 'events' && !eventsLoaded && !eventsLoading) {
      setEventsLoading(true)
      setEventsError(null)
      userClient
        .getTripEvents(id)
        .then((response) => {
          const payload = (response.data?.data || []) as WaybookEvent[]
          setEvents(Array.isArray(payload) ? payload : [])
          setEventsLoaded(true)
        })
        .catch((err: any) => {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load events'
          setEventsError(message)
        })
        .finally(() => setEventsLoading(false))
    }

    if (activeTab === 'posts' && !postsLoaded && !postsLoading) {
      setPostsLoading(true)
      setPostsError(null)
      userClient
        .getPosts({ trip_id: id, limit: 30 })
        .then((response) => {
          const payload = (response.data?.data || []) as WaybookPost[]
          setPosts(Array.isArray(payload) ? payload : [])
          setPostsLoaded(true)
        })
        .catch((err: any) => {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load posts'
          setPostsError(message)
        })
        .finally(() => setPostsLoading(false))
    }

    if (activeTab === 'files' && !filesLoaded && !filesLoading) {
      setFilesLoading(true)
      setFilesError(null)
      userClient
        .getTripFiles({ trip_id: id })
        .then((response) => {
          const payload = (response.data?.data || []) as WaybookFile[]
          setFiles(Array.isArray(payload) ? payload : [])
          setFilesLoaded(true)
        })
        .catch((err: any) => {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load files'
          setFilesError(message)
        })
        .finally(() => setFilesLoading(false))
    }

    if (activeTab === 'notes' && !notesLoaded && !notesLoading) {
      setNotesLoading(true)
      setNotesError(null)
      userClient
        .getTripNotes({ trip_id: id })
        .then((response) => {
          const payload = (response.data?.data || []) as WaybookNote[]
          setNotes(Array.isArray(payload) ? payload : [])
          setNotesLoaded(true)
        })
        .catch((err: any) => {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load notes'
          setNotesError(message)
        })
        .finally(() => setNotesLoading(false))
    }

    if (activeTab === 'participants' && !participantsLoaded && !participantsLoading) {
      setParticipantsLoading(true)
      setParticipantsError(null)
      userClient
        .getTripParticipants(id)
        .then((response) => {
          const payload = (response.data?.data || []) as WaybookParticipant[]
          setParticipants(Array.isArray(payload) ? payload : [])
          setParticipantsLoaded(true)
        })
        .catch((err: any) => {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load participants'
          setParticipantsError(message)
        })
        .finally(() => setParticipantsLoading(false))
    }
  }, [activeTab, filesLoaded, filesLoading, id, eventsLoaded, eventsLoading, notesLoaded, notesLoading, participantsLoaded, participantsLoading, postsLoaded, postsLoading])

  const tabs: Array<{ key: TripTab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'events', label: 'Events' },
    { key: 'posts', label: 'Posts' },
    { key: 'files', label: 'Files' },
    { key: 'notes', label: 'Notes' },
    { key: 'participants', label: 'Participants' },
  ]

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        {loadingTrip ? (
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading trip...
          </div>
        ) : tripError ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {tripError}
          </div>
        ) : trip ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{trip.title || 'Untitled trip'}</h1>
              {trip.description && (
                <p className="text-sm text-gray-600 mt-1 max-w-2xl">{trip.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                <span>Status: {trip.status}</span>
                <span>Privacy: {trip.privacy}</span>
                <span>Type: {trip.type}</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="px-6 pt-4 border-b border-gray-200 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition ${
              activeTab === tab.key
                ? 'bg-gray-900 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {activeTab === 'overview' && (
          <OverviewTab trip={trip} />
        )}
        {activeTab === 'events' && (
          <EventsTab loading={eventsLoading} error={eventsError} events={events} />
        )}
        {activeTab === 'posts' && (
          <PostsTab loading={postsLoading} error={postsError} posts={posts} />
        )}
        {activeTab === 'files' && (
          <FilesTab loading={filesLoading} error={filesError} files={files} />
        )}
        {activeTab === 'notes' && (
          <NotesTab loading={notesLoading} error={notesError} notes={notes} />
        )}
        {activeTab === 'participants' && (
          <ParticipantsTab loading={participantsLoading} error={participantsError} participants={participants} />
        )}
      </div>
    </div>
  )
}

function OverviewTab({ trip }: { trip: WaybookTrip | null }) {
  if (!trip) {
    return (
      <div className="text-sm text-gray-500">Trip not found.</div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900">Schedule</h3>
          <p className="mt-2 text-sm text-gray-600">
            {trip.start_date || 'Start TBD'} → {trip.end_date || 'End TBD'}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900">Locations</h3>
          {trip.locations && trip.locations.length > 0 ? (
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              {trip.locations.map((location) => (
                <li key={location.id || location.title} className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{location.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-600">No locations yet.</p>
          )}
        </div>
      </div>

      {trip.stats && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Engagement</h3>
            <div className="mt-2 grid grid-cols-2 gap-3 text-sm text-gray-600">
              <div>
                <span className="block text-xs uppercase text-gray-500">Photos</span>
                <span className="text-gray-900 font-medium">{trip.stats.photo_count ?? 0}</span>
              </div>
              <div>
                <span className="block text-xs uppercase text-gray-500">Likes</span>
                <span className="text-gray-900 font-medium">{trip.stats.likes_count ?? 0}</span>
              </div>
              <div>
                <span className="block text-xs uppercase text-gray-500">Participants</span>
                <span className="text-gray-900 font-medium">{trip.stats.participant_count ?? 0}</span>
              </div>
              <div>
                <span className="block text-xs uppercase text-gray-500">Copies</span>
                <span className="text-gray-900 font-medium">{trip.stats.copies_count ?? 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EventsTab({ loading, error, events }: { loading: boolean; error: string | null; events: WaybookEvent[] }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading events...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (events.length === 0) {
    return <div className="text-sm text-gray-500">No events yet.</div>
  }

  return (
    <div className="space-y-4">
      {events.map((event) => (
        <article key={event.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="uppercase font-semibold tracking-wide">{event.status || 'scheduled'}</span>
            <span>{new Date(event.start_date).toLocaleString()}</span>
          </div>
          <h3 className="mt-2 text-base font-semibold text-gray-900">{event.title}</h3>
          {event.description && (
            <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{event.description}</p>
          )}
          <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-3">
            {event.end_date && <span>Ends: {new Date(event.end_date).toLocaleString()}</span>}
            {event.trip_id && <span>Trip: {event.trip_id}</span>}
          </div>
        </article>
      ))}
    </div>
  )
}

function PostsTab({ loading, error, posts }: { loading: boolean; error: string | null; posts: WaybookPost[] }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading posts...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (posts.length === 0) {
    return <div className="text-sm text-gray-500">No posts linked to this trip.</div>
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <article key={post.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="uppercase font-semibold tracking-wide">{post.content_type}</span>
            <span>{new Date(post.created_at).toLocaleString()}</span>
          </div>
          {post.description && (
            <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{post.description}</p>
          )}
          <div className="mt-3 text-xs text-gray-500">
            Privacy: {post.privacy}
          </div>
        </article>
      ))}
    </div>
  )
}

function FilesTab({ loading, error, files }: { loading: boolean; error: string | null; files: WaybookFile[] }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading files...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (files.length === 0) {
    return <div className="text-sm text-gray-500">No files uploaded for this trip yet.</div>
  }

  return (
    <div className="space-y-4">
      {files.map((file) => (
        <article key={file.id} className="border border-gray-200 rounded-lg p-4 flex items-start gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">{file.filename || file.id}</h3>
            {file.description && <p className="mt-1 text-sm text-gray-600">{file.description}</p>}
            <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-3">
              <span>Uploaded: {new Date(file.created_at).toLocaleString()}</span>
              {file.mime_type && <span>Type: {file.mime_type}</span>}
              {file.size && <span>Size: {Math.round(file.size / 1024)} KB</span>}
            </div>
          </div>
          <a
            href={file.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-gray-700 hover:text-gray-900"
          >
            Open
          </a>
        </article>
      ))}
    </div>
  )
}

function NotesTab({ loading, error, notes }: { loading: boolean; error: string | null; notes: WaybookNote[] }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading notes...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (notes.length === 0) {
    return <div className="text-sm text-gray-500">No notes for this trip.</div>
  }

  return (
    <div className="space-y-4">
      {notes.map((note) => (
        <article key={note.id} className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-base font-semibold text-gray-900">{note.title}</h3>
          {note.body && (
            <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{note.body}</p>
          )}
          {note.checklist && note.checklist.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-gray-600">
              {note.checklist.map((item) => (
                <li key={item.id}>
                  <input type="checkbox" checked={item.completed} readOnly className="mr-2" />
                  {item.title}
                </li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </div>
  )
}

function ParticipantsTab({
  loading,
  error,
  participants,
}: {
  loading: boolean
  error: string | null
  participants: WaybookParticipant[]
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading participants...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </div>
    )
  }

  if (participants.length === 0) {
    return <div className="text-sm text-gray-500">No participants yet.</div>
  }

  return (
    <div className="space-y-3">
      {participants.map((participant) => (
        <div key={participant.id} className="border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-sm text-gray-600">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{participant.name || 'Unnamed participant'}</div>
            <div className="text-xs text-gray-500">Status: {participant.status || 'invited'}</div>
          </div>
        </div>
      ))}
    </div>
  )
}
